import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIsAuthenticated } from '@azure/msal-react';

import ApplicationLoader from '../../components/ApplicationLoader/ApplicationLoader';
import { PageHeaderProps } from './types';
import { BPContainer, HeaderContainer, Title } from './styles';
import BusinessPartnerSelector from '../../features/bp/BusinessPartnerSelector/BusinessPartnerSelector';
import { BusinessPartnerSelectorItem } from '../../features/bp/BusinessPartnerSelector/types';
import { useContextStore } from '../../../stores/contextStore';
import { TranslationKeys } from '../../../i18n/locales/defaultResources';
import OnSwitchingBPWarningModal from '../../features/common_modals/OnSwitchingBPWarningModal/OnSwitchingBPWarningModal';

const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  chip,
  extraButton,
}) => {
  const {
    nordzuckerInternalUser,
    businessPartners,
    activeBpNumber,
    accessibleBpRanges,
    isAccessOnBehalf,
    contextUpdating,
    switchBusinessPartner,
    finishAccessOnBehalf,
  } = useContextStore();
  const [isListOpen, setIsListOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(contextUpdating);
  const [activeBP, setActiveBP] = useState<string | undefined>(activeBpNumber);
  const [bpSelectorItems, setBpSelectorItems] = useState<
    BusinessPartnerSelectorItem[]
  >([]);
  const [aobActivationError, setAobActivationError] = useState<
    TranslationKeys | undefined
  >();
  const [aobInputValue, setAobInputValue] = useState<string | undefined>();
  const [isOnSwitchingBPWarningModalOpen, setIsOnSwitchingBPWarningModalOpen] =
    useState<boolean>(false);
  const [currentInactiveBp, setCurrentInactiveBp] = useState<
    BusinessPartnerSelectorItem | undefined
  >();
  const isBpSwitcherVisible = !isOnSwitchingBPWarningModalOpen;
  const navigate = useNavigate();
  const isAuthenticated = useIsAuthenticated();

  useEffect(() => {
    setBpSelectorItems(
      businessPartners
        .map((bpa) => {
          return {
            businessPartnerDisplayName: bpa.displayName ?? '',
            businessPartnerNumber: bpa.businessPartnerNumber ?? '',
            inactive: !bpa.activated || !bpa.initialized,
          };
        })
        .filter((bp) => bp.businessPartnerNumber)
    );
    setActiveBP(activeBpNumber);
    setAobActivationError(undefined);
    setAobInputValue(undefined);
  }, [activeBP, businessPartners]);

  useEffect(() => {
    setIsLoading(contextUpdating);
  }, [contextUpdating]);

  const handleBPSelect = (bp: BusinessPartnerSelectorItem) => {
    setIsListOpen(false);
    if (bp.inactive) {
      setIsOnSwitchingBPWarningModalOpen(true);
      setCurrentInactiveBp(bp);
      document.body.style.overflow = 'hidden';
    } else {
      setIsOnSwitchingBPWarningModalOpen(false);
      document.body.style.overflow = 'overlay';
      switchBusinessPartner(bp.businessPartnerNumber, false).then(() => {});
    }
  };

  const handleAoBEnd = () => {
    setIsListOpen(false);
    finishAccessOnBehalf().then(() => {
      navigate('/');
    });
  };

  const aobInputValueChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
    const onlyNums = event.target.value.replace(/[^0-9]/g, '');
    setAobInputValue(onlyNums);
  };

  const handleAobStart = async (bp: string) => {
    let canSwitch = false;
    if (accessibleBpRanges) {
      const parsedValue = Number.parseInt(bp);
      canSwitch = accessibleBpRanges.some((range) => {
        return (
          parsedValue >= range.low &&
          (range.high !== undefined
            ? parsedValue <= range.high!
            : parsedValue == range.low)
        );
      });
    }
    if (canSwitch) {
      try {
        await switchBusinessPartner(bp, true);
        setIsListOpen(false);
      } catch (err: unknown) {
        if (err && typeof err === 'object' && 'statusCode' in err) {
          if (err.statusCode === 'NotFound') {
            setAobActivationError(
              TranslationKeys.accessonbehalfactivationbox_error_bpnotfound
            );
          } else if (err.statusCode === 'Unauthorized') {
            setAobActivationError(
              TranslationKeys.accessonbehalfactivationbox_error_bpnotaccessible
            );
          } else {
            setAobActivationError(TranslationKeys.common_genericerror);
          }
        } else {
          setAobActivationError(TranslationKeys.common_genericerror);
        }
      }
    } else {
      setAobActivationError(
        TranslationKeys.accessonbehalfactivationbox_error_invalidbpnumber
      );
    }
  };

  return (
    <>
      <OnSwitchingBPWarningModal
        isOpen={isOnSwitchingBPWarningModalOpen}
        bp={currentInactiveBp}
        onClose={() => {
          setIsOnSwitchingBPWarningModalOpen(false);
          document.body.style.overflow = 'overlay';
        }}
      />
      {isLoading && <ApplicationLoader withBlur={true} />}{' '}
      <HeaderContainer>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Title>{title}</Title>
          {chip && <div style={{ marginLeft: '1rem' }}>{chip}</div>}
          {extraButton && (
            <div style={{ marginLeft: '16px' }}>{extraButton}</div>
          )}
        </div>
        {isAuthenticated && isBpSwitcherVisible && (
          <BPContainer
            isListOpen={isListOpen}
            hasActiveBP={!!activeBP}
            id="bp-selector"
          >
            <BusinessPartnerSelector
              isLoading={isLoading}
              hasAob={nordzuckerInternalUser}
              aobActivationError={aobActivationError}
              aobInputValue={aobInputValue}
              aobInputValueChanged={aobInputValueChanged}
              activeBPNumber={activeBP}
              bpNumbers={bpSelectorItems}
              onBPSelect={handleBPSelect}
              onAobStart={handleAobStart}
              isListOpen={isListOpen}
              setIsListOpen={setIsListOpen}
              onAobEnd={handleAoBEnd}
              isAccessOnBehalf={isAccessOnBehalf}
            />
          </BPContainer>
        )}
      </HeaderContainer>
    </>
  );
};

export default PageHeader;
